import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';
import { useAuth } from './contexts/AuthContext';
import { getReportsVariant } from './lib/permissions';
import { LivekitConnectionProvider } from './contexts/LivekitConnectionContext';
import MainLayout from './components/layout/MainLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/ui/Toast';
import { HelmetProvider } from 'react-helmet-async';
import RateLimitToastBridge from './components/RateLimitToastBridge';

// Dashboard Pages

// Test Pages
import TestLibrary from './pages/tests/TestLibrary';
import TestView from './pages/tests/TestView';

// Assessment Pages
import ResultsAnalytics from './pages/results/ResultsAnalytics';
import Assessments from './pages/assessments/Assessments';
import AssessmentCreate from './pages/assessments/AssessmentCreate';
import AssessmentBuild from './pages/assessments/AssessmentBuild';
import AssessmentEdit from './pages/assessments/AssessmentEdit';
import AssessmentReview from './pages/assessments/AssessmentReview';
import AssessmentView from './pages/assessments/AssessmentView';
import AssessmentInvite from './pages/assessments/AssessmentInvite';
import AssessmentCreatorResults from './pages/assessments/AssessmentCreatorResults';
import CandidateDetailedResults from './pages/assessments/CandidateDetailedResults';
import AssessmentResultsPage from './pages/results/AssessmentResults';

// Profile Pages
import Profile from './pages/profile/Profile';

// Public Pages (eager — candidates / shared links)
import AssessmentJoin from './pages/public/AssessmentJoin';
import AssessmentTake from './pages/public/AssessmentTake';
import AssessmentResults from './pages/public/AssessmentResults';
import ExternalAssessmentCreate from './pages/public/ExternalAssessmentCreate';
import ExternalAIInterviewCreate from './pages/public/ExternalAIInterviewCreate';

// Placeholder pages for new routes
import Billing from './pages/billing/Billing';
import SettingsPage from './pages/settings/Settings';
import Help from './pages/help/Help';
import Resources from './pages/resources/Resources';
import Library from './pages/library/Library';
import CandidateLibrary from './pages/library/CandidateLibrary';

// AI Interviews Pages
import AIInterviewJoin from './pages/public/AIInterviewJoin';
import AIInterviewPreTest from './pages/public/AIInterviewPreTest';
import AIInterviewTake from './pages/public/AIInterviewTakeLivekit';
import InterviewComplete from './pages/public/InterviewComplete';
import JobPostDetail from './pages/job-posts/JobPostDetail';
import JobScreeningsPage from './pages/screenings/JobScreeningsPage';
import ScreeningDetailPage from './pages/screenings/ScreeningDetailPage';
import SourcingSessionPage from './pages/sourcing/SourcingSessionPage';
import ScreeningApply from './pages/public/ScreeningApply';
import ScreeningResumeUpload from './pages/public/ScreeningResumeUpload';
import ScreeningDocumentUpload from './pages/public/ScreeningDocumentUpload';
import ScreeningHub from './pages/public/ScreeningHub';
import ScreeningThankYou from './pages/public/ScreeningThankYou';
import ScreeningTimeUp from './pages/public/ScreeningTimeUp';
import AIInterviewLibrary from './pages/ai/AIInterviewLibrary';
import ScheduleSlotPicker from './pages/public/ScheduleSlotPicker';

import CalendarPage from './pages/CalendarPage';
import CandidateDocumentUpload from './pages/documents/CandidateDocumentUpload';
import ArmSalaryForm from './pages/public/ArmSalaryForm';
import CandidateReviewPublic from './pages/CandidateReviewPublic';
import ReportsPage from './pages/reports/ReportsPage';
import ReportsPageMR from './pages/reports/ReportsPageMR';
import ReportsPageBK from './pages/reports/ReportsPageBK';
import { queryClient } from './lib/queryClient';

// Other Pages
import Internal from './pages/internal/Internal';
import InternalBlog from './pages/internal/InternalBlog';
import OrganizationDashboard from './pages/organization/OrganizationDashboard';
import OrganizationDepartments from './pages/organization/OrganizationDepartments';
import OrganizationUsers from './pages/organization/OrganizationUsers';
import { SITE_IN_BUILD } from './pages/karan/components/karanSite';

// Lazy: homepage + login + marketing industries pages
const Home = React.lazy(() => import('./pages/public/Home'));
const Login = React.lazy(() => import('./pages/auth/Login'));
const IndustriesOverview = React.lazy(() => import('./pages/public/IndustriesOverview'));
const IndustryComingSoon = React.lazy(() => import('./pages/public/IndustryComingSoon'));
const StoreListPage = React.lazy(() => import('./pages/stores/StoreListPage'));
const TrainingHome = React.lazy(() => import('./pages/training/TrainingHome'));
const TrainingComingSoon = React.lazy(() => import('./pages/training/TrainingComingSoon'));
const CreateTraining = React.lazy(() => import('./pages/training/CreateTraining'));
const CourseBuilder = React.lazy(() => import('./pages/training/CourseBuilder'));
const KaranHome = React.lazy(() => import('./pages/karan/KaranHome'));
const KaranNewRole = React.lazy(() => import('./pages/karan/KaranNewRole'));
const KaranRole = React.lazy(() => import('./pages/karan/KaranRole'));
// Karan's interview booking page for a candidate (public, from Karan's email; any host).
const KaranMeet = React.lazy(() => import('./pages/public/KaranMeet'));
const AssignTraining = React.lazy(() => import('./pages/training/AssignTraining'));
const LearnTraining = React.lazy(() => import('./pages/public/LearnTraining'));
const ScreeningApplicationForm = React.lazy(() => import('./pages/public/ScreeningApplicationForm'));
const CityListPage = React.lazy(() => import('./pages/cities/CityListPage'));
const Blog = React.lazy(() => import('./pages/public/Blog'));
const BlogPost = React.lazy(() => import('./pages/public/BlogPost'));
// Marketing site (redesign): lazy so its stylesheet and fonts stay out of the app bundle.
const ProductPage = React.lazy(() => import('./pages/public/ProductPage'));
const DemoRequest = React.lazy(() => import('./pages/public/DemoRequest'));
const PrivacyPolicy = React.lazy(() => import('./pages/public/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/public/TermsOfService'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
// Jodi (the Karan Arjun site, src/pages/jodi). Always on in development and on the dev site
// (karanSite SITE_IN_BUILD: dev.talentcrux.ai and the dev branch preview); other production
// builds include it only when REACT_APP_ENABLE_JODI=true, or on the site's own domain:
// REACT_APP_JODI_HOSTS lists its hostnames (comma-separated), and there "/" opens the site
// instead of the TalentCrux home page. Unset, nothing changes on talentcrux.ai.
const JodiApp = React.lazy(() => import('./pages/jodi/JodiApp'));
const JODI_HOSTS = (process.env.REACT_APP_JODI_HOSTS || '')
  .split(',')
  .map((host) => host.trim().toLowerCase())
  .filter(Boolean);
const ON_JODI_HOST =
  typeof window !== 'undefined' && JODI_HOSTS.includes(window.location.hostname.toLowerCase());
const JODI_ENABLED = SITE_IN_BUILD || ON_JODI_HOST;

const routeFallback = (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
  </div>
);

const ProtectedRoute = ({
  children,
  adminOnly = false,
  internalOnly = false,
  blogAdminOnly = false,
  featureKey = null,
}) => {
  const { isAuthenticated, isAdmin, isPlatformSuperAdmin, isBlogAdmin, loading: authLoading, user } = useAuth();
  const location = useLocation();

  if (authLoading) {
    return null;
  }

  if (!isAuthenticated || !user) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  if (internalOnly) {
    // Full super-admins get the whole portal; blog managers get in too but the
    // Internal layout restricts them to the Blogs tab only.
    if (!isPlatformSuperAdmin && !isBlogAdmin) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  }

  if (blogAdminOnly && !isBlogAdmin) {
    // Blogs page lives inside the dashboard but only blog managers may open it.
    return <Navigate to="/dashboard" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (featureKey && user?.features?.[featureKey] !== true) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const ReportsRouteGuard = () => {
  const { user } = useAuth();
  const variant = getReportsVariant(user);
  // One page per client variant — see docs/modules/reports.md. They share the route, not the file.
  if (variant === 'gocolours') return <ReportsPage />;
  if (variant === 'bk') return <ReportsPageBK />;
  if (variant === 'mr') return <ReportsPageMR />;
  return <Navigate to="/dashboard" replace />;
};

const isDev = process.env.REACT_APP_PUBLIC_URL?.includes('dev.');

function DevBanner() {
  if (!isDev) return null;
  return (
    <div
      style={{
        background: '#f59e0b',
        color: '#000',
        textAlign: 'center',
        padding: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
      }}
    >
      ⚠ DEV ENVIRONMENT — dev.talentcrux.ai
    </div>
  );
}

function App() {
  const { user } = useAuth();
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <LivekitConnectionProvider>
          <ErrorBoundary>
            <ToastProvider>
              <RateLimitToastBridge />
                <DevBanner />
                {isDev && <div style={{ height: '24px' }} />}
                <Routes>
                  {/* Public Routes */}
                  <Route
                    path="/"
                    element={
                      ON_JODI_HOST ? (
                        <Navigate to="/jodi" replace />
                      ) : (
                        <Suspense fallback={routeFallback}>
                          <Home />
                        </Suspense>
                      )
                    }
                  />
                  <Route
                    path="/industries"
                    element={
                      <Suspense fallback={routeFallback}>
                        <IndustriesOverview />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/industries/:slug"
                    element={
                      <Suspense fallback={routeFallback}>
                        <IndustryComingSoon />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/blog"
                    element={
                      <Suspense fallback={routeFallback}>
                        <Blog />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/blog/:slug"
                    element={
                      <Suspense fallback={routeFallback}>
                        <BlogPost />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/blog"
                    element={
                      <Suspense fallback={routeFallback}>
                        <Blog />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/blog/:slug"
                    element={
                      <Suspense fallback={routeFallback}>
                        <BlogPost />
                      </Suspense>
                    }
                  />
                  {JODI_ENABLED && (
                    <Route
                      path="/jodi/*"
                      element={
                        <Suspense fallback={routeFallback}>
                          <JodiApp />
                        </Suspense>
                      }
                    />
                  )}
                  <Route
                    path="/products/:slug"
                    element={
                      <Suspense fallback={routeFallback}>
                        <ProductPage />
                      </Suspense>
                    }
                  />
                  <Route path="/demo-request" element={<Suspense fallback={routeFallback}><DemoRequest /></Suspense>} />
                  <Route
                    path="/login"
                    element={
                      <Suspense fallback={routeFallback}>
                        <Login />
                      </Suspense>
                    }
                  />
                  <Route path="/assessment/:sharingLink" element={<AssessmentJoin />} />
                  <Route path="/assessment/:sharingLink/take" element={<AssessmentTake />} />
                  <Route path="/assessment/:sharingLink/results" element={<AssessmentResults />} />
                  <Route path="/external/assessment-create/:code" element={<ExternalAssessmentCreate />} />
                  <Route path="/external/ai-interview-create/:code" element={<ExternalAIInterviewCreate />} />
                  <Route path="/:companyName/apply/:screeningId" element={<ScreeningApply />} />
                  <Route path="/:companyName/apply/:screeningId/resume" element={<ScreeningResumeUpload />} />
                  <Route path="/:companyName/apply/:screeningId/documents" element={<ScreeningDocumentUpload />} />
                  <Route path="/:companyName/apply/:screeningId/application-form" element={<Suspense fallback={routeFallback}><ScreeningApplicationForm /></Suspense>} />
                  <Route path="/:companyName/apply/:screeningId/hub" element={<ScreeningHub />} />
                  <Route path="/:companyName/apply/:screeningId/thank-you" element={<ScreeningThankYou />} />
                  <Route path="/:companyName/apply/:screeningId/time-up" element={<ScreeningTimeUp />} />
                  <Route path="/apply/:screeningId" element={<ScreeningApply />} />
                  <Route path="/apply/:screeningId/resume" element={<ScreeningResumeUpload />} />
                  <Route path="/apply/:screeningId/documents" element={<ScreeningDocumentUpload />} />
                  <Route path="/apply/:screeningId/application-form" element={<Suspense fallback={routeFallback}><ScreeningApplicationForm /></Suspense>} />
                  <Route path="/apply/:screeningId/hub" element={<ScreeningHub />} />
                  <Route path="/apply/:screeningId/thank-you" element={<ScreeningThankYou />} />
                  <Route path="/apply/:screeningId/time-up" element={<ScreeningTimeUp />} />
                  <Route path="/schedule/:bookingToken" element={<ScheduleSlotPicker />} />
                  <Route path="/meet/:token" element={<Suspense fallback={routeFallback}><KaranMeet /></Suspense>} />
                  <Route path="/learn/:token" element={<Suspense fallback={routeFallback}><LearnTraining /></Suspense>} />
                  <Route path="/documents/upload/:token" element={<CandidateDocumentUpload />} />
                  <Route path="/arm-salary/fill/:token" element={<ArmSalaryForm />} />
                  <Route path="/candidate-review/:shareCode" element={<CandidateReviewPublic />} />
                  <Route path="/privacy-policy" element={<Suspense fallback={routeFallback}><PrivacyPolicy /></Suspense>} />
                  <Route path="/terms" element={<Suspense fallback={routeFallback}><TermsOfService /></Suspense>} />

                  {/* Protected Routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <MainLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<ResultsAnalytics />} />

                    <Route path="job-posts/jobs/:id" element={<JobPostDetail />} />
                    <Route
                      path="job-posts/jobs/:jobId/screenings"
                      element={<JobScreeningsPage />}
                    />
                    <Route
                      path="job-posts/jobs/:jobId/sourcing"
                      element={<JobScreeningsPage />}
                    />
                    <Route
                      path="job-posts/jobs/:jobId/onboarding"
                      element={<JobScreeningsPage />}
                    />
                    <Route
                      path="job-posts/jobs/:jobId/sourcing/:sessionId"
                      element={<SourcingSessionPage />}
                    />
                    <Route
                      path="job-posts/jobs/:jobId/screenings/:screeningId"
                      element={<ScreeningDetailPage />}
                    />

                    {/* Standalone assessment management (advanced assessment / legacy dashboard) */}
                    <Route path="assessments" element={<Assessments />} />
                    <Route path="assessments/create" element={<AssessmentCreate />} />
                    <Route path="assessments/:id/build" element={<AssessmentBuild />} />
                    <Route path="assessments/:id/edit" element={<AssessmentEdit />} />
                    <Route path="assessments/:id/review" element={<AssessmentReview />} />
                    <Route path="assessments/:id/view" element={<AssessmentView />} />
                    <Route path="assessments/:id/invite" element={<AssessmentInvite />} />
                    <Route path="assessments/:id/results" element={<AssessmentCreatorResults />} />
                    <Route path="results/assessment" element={<AssessmentResultsPage />} />
                    <Route
                      path="assessments/:id/results/:candidateEmail"
                      element={<CandidateDetailedResults />}
                    />

                    {/* Assessment Routes */}
                    <Route path="job-post" element={<ResultsAnalytics />} />

                    {/* Profile Routes */}
                    <Route path="profile" element={<Profile />} />

                    {/* Organization Routes (org admin / team admin) */}
                    <Route path="organization" element={<OrganizationDashboard />} />
                    <Route path="organization/teams" element={<OrganizationDepartments />} />
                    <Route
                      path="organization/departments"
                      element={<Navigate to="/dashboard/organization/teams" replace />}
                    />
                    <Route path="organization/users" element={<OrganizationUsers />} />

                    {/* Platform Routes */}
                    <Route path="billing" element={<Navigate to="/dashboard/profile" replace />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="help" element={<Help />} />
                    <Route path="resources" element={<Resources />} />
                    <Route
                      path="blogs"
                      element={
                        <ProtectedRoute blogAdminOnly>
                          <InternalBlog />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="calendar" element={<CalendarPage />} />
                    <Route
                      path="reports"
                      element={
                        <ProtectedRoute>
                          <ReportsRouteGuard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="library" element={<Navigate to="/dashboard/library/candidates" replace />} />
                    <Route path="library/candidates" element={<CandidateLibrary />} />
                    <Route path="library/questions" element={<Library />} />
                    <Route path="library/assessments" element={<TestLibrary />} />
                    <Route path="library/assessments/:id" element={<TestView />} />
                    <Route path="library/ai-interviews" element={<AIInterviewLibrary />} />
                    {user?.features?.store_list === true && (
                      <Route path="store-list" element={
                        <Suspense fallback={routeFallback}>
                          <StoreListPage />
                        </Suspense>
                      } />
                    )}
                    {user?.features?.training_agent === true && (
                      <>
                        <Route path="training" element={
                          <Suspense fallback={routeFallback}>
                            <TrainingHome />
                          </Suspense>
                        } />
                        <Route path="training/new" element={
                          <Suspense fallback={routeFallback}>
                            <CreateTraining />
                          </Suspense>
                        } />
                        <Route path="training/courses/:courseId" element={
                          <Suspense fallback={routeFallback}>
                            <CourseBuilder />
                          </Suspense>
                        } />
                        <Route path="training/assign" element={
                          <Suspense fallback={routeFallback}>
                            <AssignTraining />
                          </Suspense>
                        } />
                        <Route path="training/analytics" element={
                          <Suspense fallback={routeFallback}>
                            <TrainingComingSoon page="analytics" />
                          </Suspense>
                        } />
                      </>
                    )}
                    {user?.features?.karan_agent === true && (
                      <>
                        <Route path="karan" element={
                          <Suspense fallback={routeFallback}>
                            <KaranHome />
                          </Suspense>
                        } />
                        <Route path="karan/new" element={
                          <Suspense fallback={routeFallback}>
                            <KaranNewRole />
                          </Suspense>
                        } />
                        <Route path="karan/roles/:roleId" element={
                          <Suspense fallback={routeFallback}>
                            <KaranRole />
                          </Suspense>
                        } />
                      </>
                    )}
                    {user?.features?.city_list === true && (
                      <Route path="library/cities" element={
                        <Suspense fallback={routeFallback}>
                          <CityListPage />
                        </Suspense>
                      } />
                    )}
                  </Route>

                  {/* Public AI Interview Routes */}
                  <Route path="/ai-interview/:sharingLink" element={<AIInterviewJoin />} />
                  <Route
                    path="/ai-interview/:sharingLink/pre-test"
                    element={<AIInterviewPreTest />}
                  />
                  <Route path="/ai-interview/:sharingLink/take" element={<AIInterviewTake />} />
                  <Route path="/interview-complete" element={<InterviewComplete />} />

                  {/* Admin Routes */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute adminOnly>
                        <MainLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/dashboard" replace />} />
                  </Route>

                  {/* Platform internal (super admin only, after OTP login) */}
                  <Route
                    path="/internal/*"
                    element={
                      <ProtectedRoute internalOnly>
                        <Internal />
                      </ProtectedRoute>
                    }
                  />

                  {/* 404 Route */}
                  <Route path="*" element={<Suspense fallback={routeFallback}><NotFound /></Suspense>} />
                </Routes>
            </ToastProvider>
          </ErrorBoundary>
        </LivekitConnectionProvider>
      </HelmetProvider>
      <Analytics />
    </QueryClientProvider>
  );
}

export default App;
